
/**
 * processImportRows
 * Logic for validating and mapping raw rows from Excel/CSV to customer objects.
 */
export const processImportRows = (rawRows, existingCustomers = []) => {
  const validRows = [];
  const errs = [];
  const seenPhonesInFile = new Map(); // phone -> rowNum
  const existingPhones = new Set(
    (existingCustomers || [])
      .map((c) => (c.phoneNumber || c.phone || "").toString().trim())
      .filter(Boolean),
  );

  // Normalize headers: trim and lowercase keys
  const rows = (rawRows || []).map((r) => {
    const normalized = {};
    Object.keys(r).forEach((k) => {
      if (k) normalized[k.toString().trim().toLowerCase()] = r[k];
    });
    return normalized;
  });

  rows.forEach((row, i) => {
    const rowNum = i + 2;

    const firstName = (row["first name"] || row["firstname"] || row["name"] || row["customer name"] || row["customername"] || row["full name"] || "").toString().trim();
    const lastName = (row["last name"] || row["lastname"] || "").toString().trim();
    const phoneRaw = (row["phone number"] || row["phone"] || row["phonenumber"] || row["mobile"] || row["mobile number"] || row["contact"] || "").toString().trim();

    let phone = phoneRaw.replace(/\D/g, "");
    // Handle Indian prefixes (0 or 91)
    if (phone.length === 11 && phone.startsWith("0")) {
      phone = phone.substring(1);
    } else if (phone.length === 12 && phone.startsWith("91")) {
      phone = phone.substring(2);
    }

    const email = (row["email address"] || row["email"] || "").toString().trim();
    const address1 = (row["address line 1"] || row["address1"] || row["address"] || row["street"] || "").toString().trim();
    const address2 = (row["address line 2"] || row["address2"] || "").toString().trim();
    const city = (row["city"] || row["location"] || row["town"] || "").toString().trim();
    const district = (row["district"] || "").toString().trim();
    const state = (row["state"] || row["province"] || "").toString().trim();
    const pincode = (row["pin code"] || row["pincode"] || row["pin"] || "").toString().trim();
    const officeLocation = (row["office location"] || row["officelocation"] || "").toString().trim();
    const referenceType = (row["reference type"] || row["referencetype"] || "").toString().trim();
    const referenceDetails = (row["referral name / details"] || row["referral details"] || row["reference details"] || row["referencedetails"] || "").toString().trim();
    const requirementType = (row["requirement type"] || row["requirementtype"] || "").toString().trim();
    const propertyType = (row["type"] || "").toString().trim();
    const budget = parseFloat(row["estimated budget"] || row["budget"] || 0) || 0;
    const notes = (row["notes"] || "").toString().trim();

    const rowErrs = [];

    // Only skip if the row is completely empty
    if (!firstName && !lastName && !phone) return;

    if (!firstName) rowErrs.push("First Name is missing");
    if (!phone) rowErrs.push("Phone Number is missing");
    if (!city) rowErrs.push("City is missing");
    if (!state) rowErrs.push("State is missing");

    if (phone && !/^[6-9]\d{9}$/.test(phone)) {
      rowErrs.push(`Phone Number '${phone}' must be a valid 10-digit mobile number (starts with 6-9)`);
    }

    // Frontend-only check for duplicates within the current file and currently loaded list.
    if (phone && seenPhonesInFile.has(phone)) {
      rowErrs.push(`Duplicate Phone Number (already seen in Row ${seenPhonesInFile.get(phone)})`);
    }

    if (phone && existingPhones.has(phone))
      rowErrs.push("Phone Number already exists in system");

    // Track this phone so subsequent rows can detect duplicates
    if (phone && !seenPhonesInFile.has(phone)) {
      seenPhonesInFile.set(phone, rowNum);
    }

    if (rowErrs.length > 0) {
      errs.push({
        row: rowNum,
        name: firstName || `Row ${rowNum}`,
        phone: phone || "N/A",
        errors: rowErrs,
      });
    } else {
      validRows.push({
        id: Date.now() + i,
        customerName: `${firstName} ${lastName}`.trim(),
        firstName,
        lastName,
        phoneNumber: phone,
        phone,
        email,
        address: `${address1}${address2 ? ", " + address2 : ""}`.trim(),
        addressLine1: address1,
        addressLine2: address2,
        city,
        district,
        state,
        pincode,
        officeLocation,
        referenceType,
        referenceDetails,
        requirementType,
        propertyType,
        budget,
        notes,
        priority: "medium",
        source: "Bulk",
      });
    }
  });

  return { validRows, errors: errs };
};
